/**
 * The demo backend for Settings › Websites: each website's site credentials,
 * held in memory for the lifetime of one `MockApiClient`.
 *
 * It refuses what the real service refuses
 * (`modules.site_credentials.routes`), with the same status codes and in the
 * same words, because the screen's job on those paths is to show the API's
 * message — a mock that always says yes could not demonstrate that it does.
 *
 * Issued tokens are `sc_<id>.<material>`-shaped fakes; nothing here ever
 * writes one to a store or a log, matching the real credential's handling.
 */

import { ApiError } from '@/lib/api'

import type { SiteCredentialsBackend } from './api'
import type { IssuedSiteCredential, SiteCredential, SiteCredentialKind } from './types'

export interface MockSiteCredentialsOptions {
  now: () => Date
  /** The tenant's own websites, id and environment — a call for any other id is refused. */
  websites: ReadonlyArray<{ id: string; environment: SiteCredential['environment'] }>
}

function notFound(): ApiError {
  return new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
}

export class MockSiteCredentialsBackend implements SiteCredentialsBackend {
  private readonly byWebsite = new Map<string, SiteCredential[]>()
  // Starts well past any seeded id (`seed` is called with fixed ids such as
  // `sc_demo000001`), so an issued credential's id can never collide with one.
  private sequence = 1000

  constructor(private readonly options: MockSiteCredentialsOptions) {}

  /** Seeds demo credentials for a website of this tenant. */
  seed(websiteId: string, credentials: SiteCredential[]): void {
    this.environmentOf(websiteId)
    this.byWebsite.set(websiteId, credentials)
  }

  list(websiteId: string): Promise<SiteCredential[]> {
    return this.run(() => this.itemsFor(websiteId).map((credential) => ({ ...credential })))
  }

  issue(
    websiteId: string,
    label: string,
    kind: SiteCredentialKind = 'ingest',
  ): Promise<IssuedSiteCredential> {
    return this.run(() => {
      const environment = this.environmentOf(websiteId)
      this.sequence += 1
      // The API's prefixes: a read credential is `sr_`, never `sc_`.
      const id = `${kind === 'read' ? 'sr' : 'sc'}_demo${String(this.sequence).padStart(6, '0')}`
      const material = `${'demo-material-'.repeat(2)}${this.sequence}`.padEnd(32, 'x')
      const now = this.options.now().toISOString()
      const credential: SiteCredential = {
        id,
        website_id: websiteId,
        environment,
        kind,
        label,
        status: 'active',
        created_at: now,
        rotated_at: null,
        revoked_at: null,
        overlap_until: null,
      }
      this.itemsFor(websiteId).push(credential)
      return { ...credential, token: `${id}.${material}`, token_type: 'Bearer' as const }
    })
  }

  revoke(websiteId: string, credentialId: string): Promise<SiteCredential> {
    return this.run(() => {
      const credential = this.find(websiteId, credentialId)
      // Idempotent, as the API is: revoking an already-revoked credential changes nothing.
      if (credential.status !== 'revoked') {
        credential.status = 'revoked'
        credential.revoked_at = this.options.now().toISOString()
        credential.overlap_until = null
      }
      return { ...credential }
    })
  }

  private environmentOf(websiteId: string): SiteCredential['environment'] {
    const website = this.options.websites.find((candidate) => candidate.id === websiteId)
    if (!website) throw notFound()
    return website.environment
  }

  private itemsFor(websiteId: string): SiteCredential[] {
    this.environmentOf(websiteId)
    let list = this.byWebsite.get(websiteId)
    if (!list) {
      list = []
      this.byWebsite.set(websiteId, list)
    }
    return list
  }

  private find(websiteId: string, credentialId: string): SiteCredential {
    const credential = this.itemsFor(websiteId).find((candidate) => candidate.id === credentialId)
    if (!credential) throw notFound()
    return credential
  }

  /** Refusals arrive as a rejected promise, the way the HTTP client's do. */
  private run<T>(body: () => T): Promise<T> {
    try {
      return Promise.resolve(body())
    } catch (error) {
      return Promise.reject(error)
    }
  }
}
